import { useState } from 'react';
import TeacherReviewModal from '../components/TeacherReviewModal';
import styles from './StudentRow.module.scss';
import {
  Assignment,
  MultipleChoiceWork,
  StrengthsEvaluationMetawork,
  EmbeddedContent,
  Module,
} from '../../peekapak-types/LessonPlanTypes';

interface StrengthsQuestions {
  index: number;
  question1: string;
  question2: string;
  strength1: string;
  strength2: string;
}

interface StrengthsExplanation {
  strength: string;
  description: string;
}

interface StrengthsEvaluation extends EmbeddedContent {
  questions: StrengthsQuestions[];
  tiebreakerQuestions: StrengthsQuestions[];
  content: StrengthsExplanation[];
}

interface Props {
  userId: string;
  data: Module[];
  firstName: string;
  lastName: string;
  isShowStudents: boolean;
  assignments: Array<Assignment | Record<string, never>>;
}

interface StudentWork extends Assignment {
  userId: string;
  createdAt: number;
  work: {
    content: [MultipleChoiceWork];
  };
  metawork: StrengthsEvaluationMetawork;
}

interface StrengthsQuestions {
  index: number;
  question1: string;
  question2: string;
  strength1: string;
  strength2: string;
}

const getDisplayQuestions = (moduleContent: StrengthsEvaluation, metawork: StrengthsEvaluationMetawork) => {
  const displayQuestions = metawork.questionOrder.reduce((acc, curr) => {
    const question = moduleContent.questions.find((item) => item.index === curr);
    if (question) {
      return [...acc, question];
    }
    return acc;
  }, [] as StrengthsQuestions[]);
  return displayQuestions;
};

const getTiebreakerQuestions = (moduleContent: StrengthsEvaluation, metawork: StrengthsEvaluationMetawork) => {
  const tiebreakerQuestions = metawork.tiebreakerOrder.reduce((acc, curr) => {
    const question = moduleContent.tiebreakerQuestions.find((item) => item.index === curr);
    if (question) {
      return [...acc, question];
    }
    return acc;
  }, [] as StrengthsQuestions[]);
  return tiebreakerQuestions;
};

export const calculateScore = (assignment: StudentWork, moduleContent: StrengthsEvaluation) => {
  const answerArray = assignment.work.content[0].content as {
    index: number;
    answerIndex: number;
  }[];

  const combinedQuestions = [
    ...getDisplayQuestions(moduleContent, assignment.metawork),
    ...getTiebreakerQuestions(moduleContent, assignment.metawork),
  ];
  const strengthsObject = combinedQuestions.reduce(
    (acc: { [key: string]: number }, curr: StrengthsQuestions) => {
      const thisIndex = answerArray.find((item) => item.index === curr.index);
      if (!thisIndex || isNaN(thisIndex?.answerIndex)) return acc;
      if (thisIndex.answerIndex === 0)
        return acc?.[curr.strength1]
          ? { ...acc, [curr.strength1]: acc[curr.strength1] + 2 }
          : { ...acc, [curr.strength1]: 2 };
      if (thisIndex.answerIndex === 1)
        return acc?.[curr.strength1]
          ? { ...acc, [curr.strength1]: acc[curr.strength1] + 1 }
          : { ...acc, [curr.strength1]: 1 };
      if (thisIndex.answerIndex === 2)
        return acc?.[curr.strength2]
          ? { ...acc, [curr.strength2]: acc[curr.strength2] + 1 }
          : { ...acc, [curr.strength2]: 1 };
      if (thisIndex.answerIndex === 3)
        return acc?.[curr.strength2]
          ? { ...acc, [curr.strength2]: acc[curr.strength2] + 2 }
          : { ...acc, [curr.strength2]: 2 };
      return acc;
    },
    {} as { [key: string]: number },
  );
  const strengths = Object.keys(strengthsObject).filter((x) => {
    return strengthsObject[x] == Math.max.apply(null, Object.values(strengthsObject));
  });

  return strengths;
};

const BlankCell = () => {
  return (
    <div className={styles.dataCell}>
      <h4>-</h4>
    </div>
  );
};

const getTextColour = (moduleData: StrengthsEvaluation, strength: string) => {
  const index = moduleData.content.findIndex((item) => item.strength === strength);
  if (index === 0) return '#c6e4e2';
  if (index === 1) return '#fbf0b2';
  return '#e78d60';
};

const StudentRow = ({ firstName, lastName, userId, isShowStudents, assignments, data }: Props): JSX.Element => {
  const [isReviewStudentWork, setReviewStudentWork] = useState(false);
  const [modalProperties, setModalProperties] = useState({
    title: '',
    createdAt: 0,
    displayData: {},
    assetImage: '',
  });
  const handleClick = (createdAt: number, reviewContent: Module['studentContent'][0]) => {
    setModalProperties({
      title: reviewContent.title,
      createdAt: createdAt,
      displayData: reviewContent.content,
      assetImage: reviewContent.representativeImage,
    });
    setReviewStudentWork(true);
  };
  const handleCloseModal = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) setReviewStudentWork(false);
  };
  return (
    <>
      <div className={styles.container}>
        <div className={styles.nameCell}>
          <h4>{isShowStudents ? `${firstName} ${lastName ?? ''}` : 'Private'}</h4>
        </div>

        <div className={styles.unitDataContainer}>
          {assignments.map((assignment, index) => {
            if (assignment.status !== 'submitted') {
              return <BlankCell key={`${assignment.createdAt}${index}`} />;
            }
            const reviewContent =
              data &&
              data?.find((item) => item.id === assignment.module) &&
              data!.find((item) => item.id === assignment.module)!.studentContent[0];
            const moduleData =
              data &&
              data?.find((item) => item.id === assignment.module) &&
              (data!
                .find((item) => item.id === assignment.module)!
                .studentContent[0].content.find(
                  (item) => item.type === 'strengthsEvaluation',
                ) as unknown as StrengthsEvaluation);
            if (moduleData === undefined) {
              console.error('could not fined module data for assignment');
              return <BlankCell key={`${assignment.createdAt}${index}`} />;
            }
            const strength = calculateScore(assignment as StudentWork, moduleData);
            return strength.length === 1 ? (
              <div
                key={`${firstName}${index} ${assignment.createdAt}`}
                className={styles.dataCell}
                style={{
                  cursor: 'pointer',
                  backgroundColor: getTextColour(moduleData, strength.toString()),
                }}
                onClick={() => handleClick(assignment.createdAt, reviewContent!)}
              >
                <h4
                  style={
                    getTextColour(moduleData, strength.toString()) === '#e78d60'
                      ? { color: 'white' }
                      : { color: 'inherit' }
                  }
                >
                  {strength.toString()}
                </h4>
              </div>
            ) : (
              <BlankCell key={`${assignment.createdAt}${index}`} />
            );
          })}
        </div>
      </div>
      {isReviewStudentWork && (
        <div className={styles.reviewModal} onClick={(e) => handleCloseModal(e)}>
          <TeacherReviewModal
            assetImage={modalProperties.assetImage}
            studentId={userId}
            title={modalProperties.title}
            createdAt={modalProperties.createdAt}
            displayData={modalProperties.displayData}
            handleCloseModal={handleCloseModal}
            firstName={firstName}
            lastName={lastName}
          />
        </div>
      )}
    </>
  );
};

export default StudentRow;
